import React, { useState, useEffect } from "react";
import axiosInstance from "../../utils/axiosInstance";
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Stack,
  useTheme,
} from "@mui/material";

export default function AddSitemap({ closeModal, refresh, handleRefresh, chatbotId, permission }) {
  const theme = useTheme();

  useEffect(() => {
    document.title = "Add Sitemap | KlonIT Web Application";
  }, []);

  const [inputValue, setInputValue] = useState("");
  const [sitemaps, setSitemaps] = useState([]);
  const urlRegex = /^(https?:\/\/)?([\w.]+)\.([a-z]{2,})(\/[\w .-]*)*\/?$/i;
  const [error, setError] = useState(null);
  const [fetching, setFetching] = useState(false);

  const handleAddWebBtnClick = async () => {
    if (urlRegex.test(inputValue)) {
      try {
        setError(null);
        setFetching(true);

        const params = chatbotId ? { chatbot_id: chatbotId } : {};
        await axiosInstance.post(`/add-sitemap`, { sitemap: inputValue }, { params });

        setSitemaps([...sitemaps, inputValue]);
        setInputValue("");
        setError("Sitemap has been added successfully.");
        handleRefresh();
        closeModal();
      } catch (error) {
        setError(error.response?.data?.message || "Error adding Sitemap configuration.");
      } finally {
        setFetching(false);
      }
    } else {
      setError("Please enter a valid Sitemap link ending with .xml");
    }
  };

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 3 },
        backgroundColor: theme.palette.mode === "dark" ? theme.palette.background.default : "#fff",
        borderRadius: 3,
        maxWidth: 600,
        mx: "auto",
        boxShadow:
          theme.palette.mode === "dark"
            ? "0 4px 16px rgba(0,0,0,0.7)"
            : "0 4px 16px rgba(0,0,0,0.15)",
      }}
    >
      <Typography
        variant="h5"
        fontWeight={600}
        gutterBottom
        color="textPrimary"
        textAlign="center"
      >
        Add Sitemap Links
      </Typography>

      <Typography
        variant="body2"
        color="textSecondary"
        mb={3}
        textAlign="center"
      >
        Link should start with <strong>https://</strong> or <strong>www</strong> and end with{" "}
        <strong>.xml</strong>.
      </Typography>

      <Stack spacing={2}>
        <TextField
          fullWidth
          label="Sitemap URL"
          variant="outlined"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          error={!!error && !urlRegex.test(inputValue)}
          helperText={
            !!error && !urlRegex.test(inputValue)
              ? "Please enter a valid sitemap URL"
              : ""
          }
          InputProps={{
            sx: {
              backgroundColor:
                theme.palette.mode === "dark"
                  ? theme.palette.background.paper
                  : "#f9f9f9",
            },
          }}
        />

        {permission.canModify && (
          <Button
            variant="contained"
            color="primary"
            onClick={handleAddWebBtnClick}
            disabled={fetching}
            sx={{
              py: 1.2,
              fontWeight: 600,
              borderRadius: 2,
              textTransform: "none",
            }}
          >
            {fetching ? <CircularProgress size={24} color="inherit" /> : "Add Sitemap"}
          </Button>
        )}

        {error && (
          <Alert
            severity={error.includes("successfully") ? "success" : "warning"}
            sx={{
              mt: 2,
              borderRadius: 2,
              bgcolor:
                theme.palette.mode === "dark"
                  ? "rgba(255,255,255,0.08)"
                  : "rgba(255,255,255,1)",
            }}
          >
            {error}
          </Alert>
        )}
      </Stack>
    </Box>
  );
}
